/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import SortIcon from '@mui/icons-material/Sort'
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import type { Theme } from '@mui/material'
import { useState } from 'react'
import { ScheduleType } from '../../shared/types'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { FeatureFlagName } from '../../../api/feature-flags-api'
import DepositModeSummary from './deposit-mode-summary'

interface SortButtonProps {
  readonly sortOrder: 'asc' | 'desc' | null
  readonly onSort: () => void
  readonly theme: Theme
  readonly sx?: Record<string, unknown>
}

function SortButton({
  sortOrder,
  onSort,
  theme,
  sx: extraSx,
}: SortButtonProps) {
  return (
    <Tooltip
      title={(() => {
        if (sortOrder === null) return 'Sort dates'
        if (sortOrder === 'asc') return 'Sort: oldest to newest'
        return 'Sort: newest to oldest'
      })()}
    >
      <IconButton
        size="small"
        onClick={onSort}
        sx={{
          p: 0.5,
          color:
            sortOrder != null ? theme.palette.primary.dark : 'text.secondary',
          '&:hover': {
            color: theme.palette.primary.light,
            backgroundColor: theme.palette.primary.light + '20',
          },
          ...extraSx,
        }}
      >
        {sortOrder === null && <SortIcon fontSize="small" />}
        {sortOrder === 'asc' && <ArrowUpwardIcon fontSize="small" />}
        {sortOrder === 'desc' && <ArrowDownwardIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

interface PaymentScheduleTableProps {
  readonly scheduleType: ScheduleType
  readonly totalPercentage: number
  readonly children: React.ReactNode
  readonly onAddRow: () => void
  readonly onSort: (order: 'asc' | 'desc') => void
  readonly hideHeader?: boolean
}

export default function PaymentScheduleTable({
  scheduleType,
  totalPercentage,
  children,
  onAddRow,
  onSort,
  hideHeader = false,
}: Readonly<PaymentScheduleTableProps>) {
  const theme = useTheme()
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null)
  const { flags } = useFeatureFlags()
  const enablePartialMaturitiesByDollarAmount =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT]

  const handleSort = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortOrder(newOrder)
    onSort(newOrder)
  }

  return (
    <>
      {!hideHeader && (
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            color: theme.palette.primary.main,
            fontWeight: 500,
          }}
        >
          Payment Schedule
        </Typography>
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[200],
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
          position: 'relative',
        }}
      >
        {/* Table Header */}
        <Grid
          container
          sx={{
            backgroundColor: 'background.default',
            py: 1,
            borderBottom: '1px solid',
            borderColor: theme.palette.grey[200],
          }}
        >
          {enablePartialMaturitiesByDollarAmount && (
            <Grid
              size={{ md: 2 }}
              sx={{ px: 2, display: 'flex', alignItems: 'center' }}
            >
              <Typography
                variant="body2"
                fontWeight="medium"
                color={theme.palette.primary.main}
              >
                % or $
              </Typography>
            </Grid>
          )}
          <Grid
            size={{ md: 5 }}
            sx={{ px: 2, display: 'flex', alignItems: 'center' }}
          >
            <Typography
              variant="body2"
              fontWeight="medium"
              color={theme.palette.primary.main}
            >
              Payout
            </Typography>
          </Grid>
          <Grid
            size={{ md: enablePartialMaturitiesByDollarAmount ? 4 : 6 }}
            sx={{ px: 2, display: 'flex', alignItems: 'center' }}
          >
            <Typography
              variant="body2"
              fontWeight="medium"
              color={theme.palette.primary.main}
            >
              Payment Date
            </Typography>
            {!enablePartialMaturitiesByDollarAmount && (
              <SortButton
                sortOrder={sortOrder}
                onSort={handleSort}
                theme={theme}
                sx={{ ml: 1 }}
              />
            )}
          </Grid>
          {enablePartialMaturitiesByDollarAmount ? (
            <Grid
              size={{ md: 1 }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <SortButton
                sortOrder={sortOrder}
                onSort={handleSort}
                theme={theme}
              />
            </Grid>
          ) : (
            <Grid
              size={{ md: 1 }}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography variant="body2" fontWeight="medium"></Typography>
            </Grid>
          )}
        </Grid>

        {/* Payment Rows */}
        {children}

        {/* Mode-specific Summary */}
        {scheduleType === ScheduleType.DEPOSIT && (
          <DepositModeSummary totalPercentage={totalPercentage} />
        )}

        {/* Add Row Button */}
        <Grid
          container
          alignItems="center"
          onClick={onAddRow}
          sx={{
            py: 1,
            borderTop: '1px dashed',
            borderColor: theme.palette.grey[200],
            backgroundColor: 'rgba(25, 118, 210, 0.02)',
            cursor: 'pointer',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '20',
            },
          }}
        >
          <Grid size={{ md: 12 }} sx={{ px: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AddIcon
                fontSize="small"
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography
                variant="body2"
                color={theme.palette.primary.main}
                fontWeight="medium"
              >
                Add Payment Row
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
